<script lang="ts" setup>
import AudioPlay, {
  AudioPlayInstance,
} from "@/components/mobile/AudioPlay.vue";
import {
  DEPICT_MATCH_REG,
  SYMBOL_MATCH_REG,
} from "@/components/mobile/chat/chatUtils";
import ChatMsgUnlock from "@/components/mobile/chat/common/ChatMsgUnlock.vue";
import TranslateTrigger from "@/components/mobile/translate/TranslateTrigger.vue";
import { useMarkdownHTML } from "@/composable/text/useMarkdown";
import { DEFAULT_COST } from "@/enums/chatEnums";
import { IChatMessageItem } from "@/types/chat";
import { ChatMessageItemInstance } from "@/types/component";
import { useRawImageUrl } from "@/use/common";
import { showImagePreview } from "vant";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

// Props

const props = withDefaults(
  defineProps<
    IChatMessageItem & {
      maxHeight?: number | string;
      currentMateToneId?: number;
    }
  >(),
  {
    mateUuid: "",
    position: "start",
    avatarUrl: "",
    msg: "",
    name: "",
    time: "",
    reactions: () => [],
    enableReactive: false,
    enableAvatarClick: false,
    mateToneId: -1,
    enableAudio: false,
    isLocked: false,
    requireUnlock: 0,
    unlockCost: DEFAULT_COST,
  }
);

const emit = defineEmits<{
  (e: "onLongPress"): void;
  (e: "onPlayAudio", ref: HTMLAudioElement): void;
  (e: "onEdit"): void;
}>();

const router = useRouter();
const bubbleRef = ref<HTMLElement | null>(null);
const audioRef = ref<AudioPlayInstance | null>(null);
const translateResult = ref<string>("");
const maxHeight = computed(() =>
  props.maxHeight
    ? Number.isInteger(props.maxHeight)
      ? `${props.maxHeight}px`
      : props.maxHeight
    : undefined
);

const { html: contentHtml } = useMarkdownHTML(() => props.msg, {
  escapeHTML: false,
  transformBeforeLastBreak: props.enableParseMd,
});
const { html: translateHtml } = useMarkdownHTML(translateResult, {
  escapeHTML: false,
});
const audioMsg = computed(() => {
  // 过滤掉描述性文本和包裹的符号
  return props.msg
    .replaceAll(DEPICT_MATCH_REG, "")
    .replaceAll(SYMBOL_MATCH_REG, "")
    .trim();
});

const onAvatarClick = () => {
  if (props.enableAvatarClick) {
    router.push({ name: "mateHome", params: { uuid: props.mateUuid } });
  }
};

const clickImageMessage = () => {
  showImagePreview([useRawImageUrl(props.media!.url!)]);
};
defineExpose<ChatMessageItemInstance>({
  id: props.id,
  onAvatarClick,
  audioRef,
});
</script>
<template>
  <div
    :class="{
      'chat-start': position === 'start',
      'chat-end': position === 'end',
    }"
    class="chat relative without-avatar chat-v2"
  >
    <div
      ref="bubbleRef"
      :class="{
        'border border-pink-500/50': requireUnlock,
        dark: props.position === 'end',
        light: props.position === 'start',
      }"
      class="chat-msg-item chat-container text-sm"
    >
      <slot>
        <div class="flex flex-col gap-2">
          <div
            v-html="
              contentHtml || media
                ? `${contentHtml}${isLocked ? '...' : ''}`
                : `[${$t('Message.newsSeemsMissing')}]`
            "
          />

          <div v-if="media" class="w-[10rem]" @click="clickImageMessage">
            <ImageCard
              :is-nsfw="media.isNsfw"
              :media="media"
              crossorigin="anonymous"
            />
          </div>
        </div>
        <div
          v-if="translateHtml"
          v-html="`${translateHtml}${isLocked ? '...' : ''}`"
        />
        <ChatMsgUnlock
          v-if="isLocked"
          :msgId="id!"
          :unlock-cost="unlockCost"
          color-mode="light"
        />
      </slot>
    </div>
    <div
      v-if="enableReactive && !isLocked"
      class="mt-1 flex gap-1 items-center"
    >
      <div class="join rounded-full overflow-hidden glass">
        <button
          class="btn btn-ghost btn-xs join-item shrink-0 text-white w-8 btn-square"
          @click="emit('onEdit')"
        >
          <i class="iconify mingcute--edit-line" />
        </button>
        <TranslateTrigger
          v-if="msg"
          v-model:result="translateResult"
          :translate-target="msg"
          class="btn-ghost btn-xs join-item shrink-0 w-8 text-white"
        />
        <button
          class="btn btn-ghost btn-xs join-item shrink-0 text-white w-8 btn-square"
          @click="emit('onLongPress')"
        >
          <i class="iconify mingcute--more-1-fill" />
        </button>
      </div>
    </div>

    <AuthLock
      v-if="enableAudio && !isLocked && mateToneId > 0 && audioMsg.length > 0"
      :type="2"
      class="left-0 -top-2"
      style="position: absolute"
    >
      <audio-play
        :id="id"
        ref="audioRef"
        :mate-tone-id="mateToneId"
        :msg="audioMsg"
        :new-mate-tone-id="currentMateToneId"
        :url="audioUrl"
        icon-type="play"
        is-chat
        playBtnClass="btn btn-base btn-primary drop glass"
        @on-play-audio="emit('onPlayAudio', $event)"
      />
    </AuthLock>
  </div>
</template>

<style lang="postcss">
.chat {
  column-gap: 0.9rem;
}

.without-avatar.chat-end,
.without-avatar.chat-start {
  @apply grid-cols-[1fr];
}

.chat-msg-item {
  @apply p-4 rounded-xl relative overflow-hidden backdrop-blur-md max-w-[90%] overflow-y-auto max-h-[var(--maxHeight)];
  --maxHeight: v-bind(maxHeight);
  max-height: var(--maxHeight);
}

.chat-start .chat-msg-item {
  @apply bg-white/80 text-black;
}

.chat-end .chat-msg-item {
  @apply bg-black/50 text-white;
}

.chat-v2 .chat-msg-item {
  max-width: 100%;
}

.chat-v2.chat-start .chat-msg-item .desc-msg,
.chat-v2.chat-end .chat-msg-item .desc-msg {
  @apply text-black;
}

.chat-msg-item img {
  @apply max-w-[13rem];
}

.chat-msg-item p:last-child {
  display: inline;
}

.chat-start .chat-msg-item em {
  @apply text-zinc-500;
}

.chat-end .chat-msg-item em {
  @apply text-blue-300;
}
</style>
